import { useEffect, useMemo, useRef, useState, forwardRef, useImperativeHandle } from "react";
import dayjs from "dayjs";
import { useInterval } from "../../../../utils/UseInterval";

import { Box, Button, Chip, FormControlLabel, Stack, Switch, Tooltip, Typography } from "@mui/material";

import { GiftItem, userState } from "../../../../interface/MainInterface";
import { HttpAdminApi } from "../../../../interface/admin-api";
import LoadingCircle from "../../../../utils/LoadingCircle";
import Toast from "../../../../utils/Toast";
import EventUserScrollList, { EventUserScrollColumn, compareApplyDtm } from "./EventUserScrollList";
import { usePrevEventWinners } from "./usePrevEventWinners";

interface propsType {
  userState: userState;
  channelInfo: any;
  eventInfo: any;
  callback: any;
}

const adminApi = new HttpAdminApi();

// 서버에서 한 번에 받아오는 응모자 수(서버 최대 1000). 응모자가 많은 이벤트에서 한 번에 전체 조회는 Lambda 응답(6MB)·API Gateway(29초) 한도를 넘긴다
const fetchLimit: number = 1000;
// Toast 는 위치를 꼭 받는다 (없으면 position.vertical 을 읽다가 예외가 나서 메시지가 뜨지 않는다)
const toastPosition = { vertical: "top", horizontal: "center" };

const LiveEventApplyList = (props: propsType, ref: any) => {
  const toastRef: any = useRef();
  const [isLoading, setIsLoading] = useState(false);

  // 응모시간 오름차순 응모자 목록. 신규 응모자는 증분 조회로 맨 아래에 붙는다
  const [applyList, setApplyList] = useState<any[]>([]);
  const [isListLoading, setIsListLoading] = useState(false);
  // 다음 증분 조회의 시작 시각(서버가 돌려준 값). 빈 문자열이면 전체 조회가 아직 끝나지 않았다
  const applyDtmFromRef = useRef("");
  // 이벤트가 바뀌면 이전 조회 결과를 버리기 위한 순번
  const loadSeqRef = useRef(0);
  // 조회가 겹치지 않게 막는다
  const busyRef = useRef(false);

  // 같은 방송의 이전 이벤트 당첨자. 표시만 하고 추첨 대상에서 빼지는 않는다
  const prevWinners = usePrevEventWinners(props.channelInfo.broad_seq, props.eventInfo);
  const [showPrevWinnersOnly, setShowPrevWinnersOnly] = useState(false);

  // 기당첨자만 걸러 볼 때도 전체 목록 기준 순번을 보여준다
  const rowNoMap = useMemo(
    () => new Map(applyList.map((row, idx) => [row.sk, idx + 1] as [string, number])),
    [applyList]
  );
  const prevWinnerList = useMemo(
    () => applyList.filter((row) => prevWinners.has(row.user_id)),
    [applyList, prevWinners]
  );

  const exceptClassName = (row: any) => (row.except ? "line-through color-gray" : "");

  const columns: EventUserScrollColumn[] = [
    {
      field: "user_id",
      headerName: "아이디",
      renderCell: (row: any) => {
        const prevWins = prevWinners.get(row.user_id);
        return (
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            {prevWins && (
              <Tooltip
                title={prevWins.map((win, idx) => (
                  <div key={idx}>{`${win.evntNm} : ${win.ofrNm}`}</div>
                ))}
              >
                <Chip label="기당첨" size="small" color="warning" sx={{ flexShrink: 0 }} />
              </Tooltip>
            )}
            <Typography variant="body2" noWrap className={exceptClassName(row)} sx={{ minWidth: 0 }}>
              {row.user_id}
            </Typography>
          </Stack>
        );
      },
    },
    {
      field: "cust_no",
      headerName: "고객번호",
      renderCell: (row: any) => (
        <Typography variant="body2" noWrap className={exceptClassName(row)}>
          {row.cust_no}
        </Typography>
      ),
    },
    {
      field: "apply_dtm",
      headerName: "응모시간",
      renderCell: (row: any) => (
        <Typography variant="body2" noWrap className={exceptClassName(row)}>
          {dayjs(row.apply_dtm).format("YYYY-MM-DD HH:mm:ss")}
        </Typography>
      ),
    },
    {
      field: "except",
      headerName: "",
      width: 120,
      renderCell: (row: any) => (
        <Button
          variant={row.except ? "contained" : "outlined"}
          color={row.except ? "primary" : "error"}
          onClick={() => {
            update_apply_flag(row, "except_winner", !row.except, false);
          }}
        >
          {row.except ? "초기화" : "대상 제외하기"}
        </Button>
      ),
    },
    {
      field: "accept",
      headerName: "",
      width: 120,
      renderCell: (row: any) => (
        <Button
          variant={row.accept ? "contained" : "outlined"}
          color={row.accept ? "primary" : "error"}
          onClick={() => {
            update_apply_flag(row, "accept_winner", false, !row.accept);
          }}
        >
          {row.accept ? "초기화" : "수기 당첨자"}
        </Button>
      ),
    },
  ];

  // 추첨 대상 제외(except_winner) / 수기 당첨자 선정(accept_winner) 및 해제.
  // 서버가 보낸 행을 그대로 다시 저장하므로 화면의 행은 바꾸지 않고 복사본을 보낸 뒤, 성공하면 그 행만 갱신한다.
  // 서버는 저장에 실패해도 code 200 을 주므로 response.result 로 저장 여부를 확인한다
  const update_apply_flag = async (row: any, command: string, except: boolean, accept: boolean) => {
    const res = await adminApi.post({ ...row, command: command, except: except, accept: accept });
    if (res.code === "200" && res.response?.result) {
      setApplyList((prev) => prev.map((item) => (item.sk === row.sk ? { ...item, except: except, accept: accept } : item)));
    } else {
      console.error(`${command} : `, res.response?.error_msg);
      toastRef.current?.toast("저장하지 못했습니다. 다시 시도해 주세요.", "error", 3000, toastPosition);
    }
  };

  // 응모자 전체 조회. 서버 페이지는 sk(고객번호 해시) 순이라 전부 받은 뒤 응모시간 순으로 정렬한다
  const load_apply_list = async () => {
    const loadSeq = ++loadSeqRef.current;
    busyRef.current = true;
    applyDtmFromRef.current = "";
    setApplyList([]);
    setIsListLoading(true);

    try {
      let rows: any[] = [];
      let lastKey = "";
      let applyDtmFrom = "";
      do {
        const res = await adminApi.post({
          command: "get_apply_list",
          event_seq: props.eventInfo.sk,
          limit: fetchLimit,
          last_key: lastKey,
        });
        if (loadSeq !== loadSeqRef.current) return;
        if (res.code !== "200") {
          console.error("get_apply_list : ", res.response?.error_msg);
          return;
        }
        // 첫 페이지를 조회한 시각부터 이어서 받아야 조회하는 동안 들어온 응모자도 빠지지 않는다
        if (!applyDtmFrom) applyDtmFrom = res.response.next_apply_dtm_from;
        rows = rows.concat(res.response.apply_list || []);
        lastKey = res.response.last_key || "";
      } while (lastKey);

      applyDtmFromRef.current = applyDtmFrom;
      setApplyList(rows.sort(compareApplyDtm));
    } finally {
      if (loadSeq === loadSeqRef.current) {
        busyRef.current = false;
        setIsListLoading(false);
      }
    }
  };

  // 증분 조회. 직전 조회 이후 응모자만 받아 목록에 붙인다 (서버가 겹쳐 읽은 응모자는 sk 로 걸러낸다)
  const poll_apply_list = async () => {
    const loadSeq = loadSeqRef.current;
    busyRef.current = true;

    try {
      const res = await adminApi.post({
        command: "get_apply_list",
        event_seq: props.eventInfo.sk,
        apply_dtm_from: applyDtmFromRef.current,
      });
      if (loadSeq !== loadSeqRef.current) return;
      if (res.code !== "200") {
        console.error("get_apply_list : ", res.response?.error_msg);
        return;
      }
      applyDtmFromRef.current = res.response.next_apply_dtm_from;

      const fetched: any[] = res.response.apply_list || [];
      if (fetched.length === 0) return;
      setApplyList((prev) => {
        const known = new Set(prev.map((item) => item.sk));
        const added = fetched.filter((item) => !known.has(item.sk));
        return added.length === 0 ? prev : [...prev, ...added].sort(compareApplyDtm);
      });
    } finally {
      if (loadSeq === loadSeqRef.current) busyRef.current = false;
    }
  };

  // 추첨하기 (서버에서 추첨한다. 응모자가 많으면 브라우저로 전체 목록을 내려받는 방식은 한도를 넘어 실패한다)
  const draw_event = async () => {
    const ofrList = props.eventInfo.event_condition.ofrList || [];
    const totalPrizeCount = ofrList.reduce((sum: number, gift: GiftItem) => sum + (Number(gift.offrQty) || 0), 0);

    if (totalPrizeCount === 0) {
      toastRef.current?.toast("경품이 등록되어 있지 않습니다.", "error", 3000, toastPosition);
      return;
    }

    if (!window.confirm(`총 ${totalPrizeCount}명(수기 당첨자 포함)을 추첨하시겠습니까?`)) return;

    const param: any = {
      command: "draw_winners",
      user_id: props.userState.id,
      broad_seq: props.channelInfo.broad_seq,
      event_info: {
        event_seq: props.eventInfo.sk,
      },
    };

    setIsLoading(true);
    const res = await adminApi.post(param);
    setIsLoading(false);

    if (res.code === "200") {
      props.callback({ command: "draw_event", info: props.eventInfo, drawResult: res.response.draw_result });
    } else {
      console.error("draw_winners : ", res.response?.error_msg);
      toastRef.current?.toast(res.response?.error_msg || "추첨 중 오류가 발생했습니다.", "error", 3000, toastPosition);
    }
  };

  useEffect(() => {
    if (props.eventInfo?.sk !== undefined) load_apply_list();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.eventInfo]);

  useEffect(() => {
    setShowPrevWinnersOnly(false);
  }, [props.eventInfo.sk]);

  const delay: number = 3000; // 3초마다 변경
  useInterval(
    () => {
      if (busyRef.current) return;
      // 전체 조회가 실패했으면 다시 시도하고, 끝났으면 그 뒤 응모자만 받는다
      if (applyDtmFromRef.current) poll_apply_list();
      else load_apply_list();
    },
    props.eventInfo.event_status === "START" ? delay : null
  );

  // 부모 Component에서 접근 가능하도록 함수 전달.
  useImperativeHandle(ref, () => ({
    draw_event,
  }));

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <Typography variant="body2">
          {isListLoading
            ? "응모자 목록을 불러오는 중입니다."
            : `응모자 총 ${applyList.length}명 · 이전 이벤트 당첨자 ${prevWinnerList.length}명`}
        </Typography>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showPrevWinnersOnly}
              onChange={(event) => setShowPrevWinnersOnly(event.target.checked)}
            />
          }
          label={<Typography variant="body2">기당첨자만 보기</Typography>}
          sx={{ mr: 0 }}
        />
      </Box>
      {/* 필터를 바꾸면 목록을 새로 그려 1번부터 보여준다 */}
      <EventUserScrollList
        key={`${props.eventInfo.sk}-${showPrevWinnersOnly}`}
        rows={showPrevWinnersOnly ? prevWinnerList : applyList}
        columns={columns}
        emptyText={isListLoading ? "" : showPrevWinnersOnly ? "이전 이벤트 당첨자가 없습니다." : "응모자가 없습니다."}
        getRowId={(row) => row.sk}
        getRowNo={(row) => rowNoMap.get(row.sk)}
        isRowHighlighted={(row) => prevWinners.has(row.user_id)}
        newRowsLabel="최신 응모자로 이동"
        onRowClick={(row) => props.callback({ command: "select_row", info: row })}
      />
      <LoadingCircle loading={isLoading} />
      <Toast ref={toastRef} />
    </>
  );
};

export default forwardRef(LiveEventApplyList);
